import { lazy, Suspense, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import MainLogin from "./components/Login/MainLogin";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import "./App.css";
import LoadingSpinner from "./components/Universal/LoadingSpinner";
import ErrorBoundary from "./components/Universal/ErrorBoundary";

// COLD-START SPLIT (2026-09-03): the login screen used to download the
// ENTIRE app bundle before it could paint. Main is its own chunk now —
// login ships only the shell — and the chunk starts downloading in the
// background the moment the login screen mounts, so by the time
// credentials are in, the app is usually already parked in the HTTP cache.
const Main = lazy(() => import("./Main"));

const CenteredWait = ({ label }) => (
  <div className="flex flex-row gap-x-2 flex-grow justify-center items-center h-screen w-full bg-mainBg font-poppins font-light text-mainText">
    <LoadingSpinner />
    {label}
  </div>
);

const AppRoutes = () => {
  const { isUserLoggedIn, loading } = useAuth();

  // Warm the Main chunk while the user is still authenticating/typing.
  useEffect(() => {
    if (!isUserLoggedIn) import("./Main").catch(() => {});
  }, [isUserLoggedIn]);

  if (loading) {
    return <CenteredWait label="Authenticating..." />;
  }

  return (
    <Routes>
      {isUserLoggedIn ? (
        <>
          <Route path="/" element={<Navigate to="/map" replace />} />
          <Route
            path="/*"
            element={
              <Suspense fallback={<CenteredWait label="Loading..." />}>
                <Main />
              </Suspense>
            }
          />
          <Route path="/login" element={<Navigate to="/map" replace />} />
        </>
      ) : (
        <>
          <Route path="/" element={<MainLogin />} />
          <Route path="/login" element={<MainLogin />} />
          <Route path="/*" element={<Navigate to="/login" replace />} />
        </>
      )}
    </Routes>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <ThemeProvider>
        <Router>
          {/* Catch of last resort (A1): a crash below cannot take down auth
              or theming; page-level boundaries in Main.jsx catch first. */}
          <ErrorBoundary>
            <AppRoutes />
          </ErrorBoundary>
        </Router>
      </ThemeProvider>
    </AuthProvider>
  );
};

export default App;
